import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';
import { formatStatusLabel } from '../../../utils/formatters';
import { StatusOverview } from '../../../types/utility.types';

interface StatusOverviewChartProps {
  data: StatusOverview[];
}

// Direct status color mapping - matches actual backend status values
const STATUS_COLORS: Record<string, string> = {
  'LIVE': '#4caf50', // Green - Active contracts
  'PENDING': '#ff9800', // Orange - Awaiting action
  'LOCKED IN / AWAITING LIVE': '#2196f3', // Blue - Confirmed, waiting to go live
  'SIGNED - PENDING': '#00bcd4', // Cyan - Signed but not yet live
  'RENEWED AWAITING LIVE': '#03a9f4', // Light Blue - Renewal in progress
  'COT / COO': '#9c27b0', // Purple - Change of Tenancy/Occupancy
  'EXPIRED': '#795548', // Brown - Contract ended naturally
  'CANCELLED': '#f44336', // Red - Contract cancelled
  'TERMINATED PRIOR TO END': '#e91e63', // Pink - Early termination
  'TERMINATED': '#f44336', // Red - General termination
  'LOST': '#607d8b', // Blue Grey - Lost opportunity
  'OBJECTION': '#ff5722', // Deep Orange - Customer objection
  'DRAFT': '#9e9e9e', // Grey - Draft status
  'default': '#64748B' // Slate gray for unknown statuses
};

// Define consistent ordering for statuses
const STATUS_ORDER = [
  'LIVE',
  'LOCKED IN / AWAITING LIVE',
  'RENEWED AWAITING LIVE',
  'SIGNED - PENDING',
  'PENDING',
  'COT / COO',
  'EXPIRED',
  'CANCELLED',
  'TERMINATED PRIOR TO END',
  'TERMINATED',
  'LOST',
  'OBJECTION',
  'DRAFT'
];

const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.default;
};

const StatusOverviewChart: React.FC<StatusOverviewChartProps> = ({ data }) => {
  const navigate = useNavigate();

  // Process and sort data by predefined order
  const chartData = data
    .map((item) => ({
      id: item._id.status, // Use status as stable ID to prevent cycling during refresh
      value: item.count,
      label: formatStatusLabel(item._id.status),
      color: getStatusColor(item._id.status),
      originalStatus: item._id.status,
      sortOrder: STATUS_ORDER.indexOf(item._id.status.toUpperCase())
    }))
    .sort((a, b) => {
      // Sort by predefined order, putting unknown statuses at the end
      const aOrder = a.sortOrder === -1 ? 999 : a.sortOrder;
      const bOrder = b.sortOrder === -1 ? 999 : b.sortOrder;
      return aOrder - bOrder;
    });

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
        Click on a legend item to view contracts
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', height: 300 }}>
        <Box sx={{ flex: 1, height: '100%' }}>
          <PieChart
            series={[
              {
                data: chartData.map(item => ({
                  ...item,
                  color: item.color
                })),
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30 },
              },
            ]}
            height={300}
            margin={{ right: 20 }}
            legend={{ hidden: true }}
            sx={{
              '& .MuiPopper-root': {
                '& .MuiChartsTooltip-root': {
                  background: 'rgba(30, 41, 59, 0.95) !important',
                  backdropFilter: 'blur(12px) !important',
                  WebkitBackdropFilter: 'blur(12px) !important',
                  border: '1px solid rgba(255, 255, 255, 0.1) !important',
                  borderRadius: '8px !important',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3) !important',
                },
                '& .MuiChartsTooltip-table': {
                  color: 'rgba(255, 255, 255, 0.9) !important',
                },
                '& .MuiChartsTooltip-cell': {
                  color: 'rgba(255, 255, 255, 0.9) !important',
                }
              }
            }}
          />
        </Box>
        <Box sx={{ 
          width: 120, 
          ml: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1
        }}>
          {chartData.map((item, index) => (
            <Box
              key={item.id}
              onClick={() => {
                console.log('Manual legend click:', item);
                const statusParam = item.originalStatus;
                console.log('Navigating to:', `/utilities/contracts?status=${encodeURIComponent(statusParam)}`);
                navigate(`/utilities/contracts?status=${encodeURIComponent(statusParam)}`);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '2px 4px',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  backgroundColor: item.color,
                  borderRadius: '2px',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ 
                fontSize: 12, 
                color: 'white',
                lineHeight: 1.2,
                fontWeight: 400
              }}>
                {item.label} ({item.value})
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export { StatusOverviewChart };
